import React from 'react';
import { Topic } from '../types';

const Table: React.FC<{ headers: string[]; data: (string | React.ReactNode)[][]; }> = ({ headers, data }) => (
    <div className="overflow-x-auto my-4 rounded-lg shadow-md">
      <table className="min-w-full bg-white border border-gray-200">
        <thead className="bg-sky-100">
          <tr>
            {headers.map((header, index) => (
              <th key={index} className="p-2 sm:py-3 sm:px-4 border-b border-gray-200 text-left text-xs sm:text-sm font-semibold text-sky-800 uppercase tracking-wider">{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex} className="hover:bg-sky-50 transition-colors">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="p-2 sm:py-3 sm:px-4 border-b border-gray-200 text-slate-700 text-sm">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  
const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <code className="bg-sky-100 text-sky-900 font-semibold font-mono rounded-md px-1.5 py-0.5 text-sm">{children}</code>
);


export const TOPICS: Topic[] = [
    {
        id: 'arsenic',
        title: 'আর্সেনিক (Arsenic) দূষণ',
        content: <>
            <p className="mb-4">আর্সেনিক (Arsenic) দূষণ এবং মানবদেহে এর প্রভাব সম্পর্কে উৎসগুলোতে বিস্তারিত তথ্য দেওয়া আছে।</p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">১. স্বাস্থ্যগত প্রভাব ও লক্ষণ</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li>মানবদেহে আর্সেনিকের বিষক্রিয়ার ফলে ত্বকে সৃষ্ট প্রভাবের চিত্র উৎসসমূহে দেখানো হয়েছে।</li>
                <li>আর্সেনিক (<Code>As</Code>) বিষক্রিয়ার একটি সুপরিচিত ফল হলো ব্ল্যাক ফুট ডিজিজ (Black Foot Disease)।</li>
                <li><Code>As</Code> এর বিষক্রিয়ায় চামড়ার উপর ছোট ছোট কালো ফুস্কুরি সৃষ্টি হতে পারে।</li>
                <li>আর্সেনিক <Code>DNA</Code>-তে ক্ষতিকারক প্রভাব সৃষ্টি করে, যা ক্যান্সার (Carcinogenic effects) সৃষ্টিতে ভূমিকা রাখে।</li>
                <li>এটি প্রজনন ক্ষতিকারক প্রভাব (Reproductive effects) এবং মিউটেজেনিক প্রভাব (Mutagenic effects) সৃষ্টি করে।</li>
            </ul>
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">২. আর্সেনিকের বিষক্রিয়া প্রক্রিয়া</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
               <li>আর্সেনিক ফসফেট-এর সাথে সাদৃশ্যপূর্ণ হওয়ায় এটি ফসফেট-এর প্রতিস্থাপক হিসেবে কাজ করে।</li>
                <li>আর্সেনিক ফসফেটের সাথে সাদৃশ্যপূর্ণ হওয়ায় এটি <Code>ATP</Code> তৈরিতে বাধা দেয় এবং কোষের শ্বসন প্রক্রিয়াকে ব্যাহত করে।</li>
                <li>আর্সেনিক মানবদেহের প্রোটিন অণু, বিশেষত সালফহাইড্রিল গ্রুপ (<Code>-SH</Code> গ্রুপ)-এর সাথে বন্ধন তৈরি করে বিষক্রিয়া ঘটায়।</li>
                <li>আর্সেনিক সাধারণত <Code>As³⁺</Code> বা আর্সেনিক (III) জারণ অবস্থাতেই বিষাক্ত হয়ে থাকে।</li>
            </ul>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">৩. নিরাপত্তা ও সহনশীল মাত্রা</h3>
            <Table headers={["বিষয়", "মাত্রা"]} data={[
                ["বিশ্ব স্বাস্থ্য সংস্থা (WHO) কর্তৃক অনুমোদিত নিরাপদ মাত্রা", "0.01 mg/L বা 0.01 ppm"],
                ["বাংলাদেশ সরকারের কর্তৃক অনুমোদিত সর্বোচ্চ গ্রহণীয় মাত্রা", "0.05 mg/L বা 0.05 ppm"],
                ["মানবদেহের সর্বোচ্চ সহনশীল মাত্রা (Threshold Limiting Value - TLV)", "0.05 mg/L বা 0.05 ppm"]
            ]} />
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">৪. পানিতে আর্সেনিকের উপস্থিতি</h3>
             <p>পানীয় জলে আর্সেনিক সাধারণত আর্সেনাইট (<Code>As³⁺</Code>) বা আর্সেনেট (<Code>As⁵⁺</Code>) আয়ন রূপে দ্রবীভূত অবস্থায় থাকে।</p>
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">৫. আর্সেনিক অপসারণ পদ্ধতি</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li>আর্সেনিক দূষণমুক্ত করার জন্য ব্যবহৃত পদ্ধতিগুলোর মধ্যে রয়েছে রাসায়নিক অধঃক্ষেপণ (coagulation), বিপরীত অভিস্রবণ (reverse osmosis), এবং আয়ন বিনিময় (ion exchange)।</li>
                <li>রাসায়নিক অধঃক্ষেপণ পদ্ধতিতে ফেরিক ক্লোরাইড (<Code>FeCl₃</Code>) বা ফিটকিরি (alum) ব্যবহার করা হয়।</li>
            </ul>
        </>
    },
    {
        id: 'thermochemistry',
        title: 'তাপীয় রসায়ন (Thermochemistry)',
        content: <>
            <p className="mb-4">উৎসসমূহে তাপীয় রসায়ন (Thermochemistry) এবং তাপমাত্রা সম্পর্কিত বিভিন্ন গুরুত্বপূর্ণ তথ্য, ধারণা ও সূত্রাবলী পাওয়া যায়।</p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">১. তাপমাত্রা ও পরম শূন্য</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>কক্ষ তাপমাত্রা (Room Temperature):</strong> <Code>25°C</Code> বা <Code>298 K</Code> তাপমাত্রাকে কক্ষ তাপমাত্রা বলা হয়।</li>
                <li><strong>পরম শূন্য তাপমাত্রা (Absolute Zero Temperature):</strong> সেলসিয়াস স্কেলে পরম শূন্য তাপমাত্রার মান হলো <Code>-273.15°C</Code> বা <Code>-273°C</Code>। কেলভিন স্কেলে এর মান <Code>0 K</Code>।</li>
                <li><strong>আদর্শ গ্যাসের গতিশক্তি:</strong> <Code>Eₖ = ³/₂ RT</Code>।</li>
                <li><strong>জলের ত্রৈধ বিন্দু (Triple point of water):</strong> পানির ত্রৈধ বিন্দুর তাপমাত্রা হলো <Code>273.16 K</Code>।</li>
            </ul>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">২. তাপগতিবিদ্যার সূত্রাবলী</h3>
             <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>প্রথম সূত্র (First Law):</strong> এটি তাপশক্তি ও যান্ত্রিক শক্তি সম্পর্কিত।</li>
                <li><strong>দ্বিতীয় সূত্র (Second Law):</strong> তাপ সম্পূর্ণরূপে কাজে পরিণত করা যায় না।</li>
             </ul>
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">৩. তাপীয় বিক্রিয়া ও এনথালপি পরিবর্তন</h3>
             <Table headers={["বিক্রিয়ার প্রকৃতি", "ব্যাখ্যা", "এনথালপি পরিবর্তন (ΔH)", "উদাহরণ", "উৎস"]} data={[
                 ["তাপহারী (Endothermic)", "বিক্রিয়ায় তাপ শোষণ হয়", "ΔH > 0", <><Code>N₂ + O₂ ⇌ 2NO</Code></>, ""],
                 ["তাপোৎপাদী (Exothermic)", "বিক্রিয়ায় তাপ উৎপন্ন হয়", "ΔH < 0", <><Code>C + O₂ = CO₂</Code></>, ""],
             ]} />
             <p className="mt-4"><strong>নির্দিষ্ট তাপসমূহ:</strong></p>
             <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>প্রশমন তাপ:</strong> শক্তিশালী অ্যাসিড ও ক্ষারকের জন্য <Code>57.34 kJ</Code>।</li>
                <li><strong>পানির গলন তাপ:</strong> <Code>+6.0 kJ mol⁻¹</Code>।</li>
                <li><strong>পানির বাষ্পীভবন তাপ:</strong> <Code>+44.0 kJ mol⁻¹</Code>।</li>
                <li><strong>বন্ধন শক্তি:</strong> <Code>H-H</Code> বন্ধন শক্তি <Code>435.5 kJ mol⁻¹</Code>।</li>
             </ul>
        </>
    },
    {
        id: 'environmental-impacts',
        title: 'রাসায়নিকের পরিবেশগত প্রভাব',
        content: <>
            <p className="mb-4">বিভিন্ন রাসায়নিক দ্রব্যের পরিবেশ ও মানব স্বাস্থ্যের উপর প্রতিক্রিয়া নিচে তুলে ধরা হলো।</p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">১. বায়ু দূষণ এবং জলবায়ুগত প্রভাব</h3>
            <Table headers={["রাসায়নিক দ্রব্য", "পরিবেশগত প্রভাব", "স্বাস্থ্যগত প্রভাব/অন্যান্য তথ্য"]} data={[
                [<Code>CO₂</Code>, "প্রধান গ্রিনহাউস গ্যাস (৫০% ভূমিকা)", "-"],
                ["CFCs", "ওজোন স্তর ক্ষয় করে, গ্রিনহাউস প্রভাবে ১৬% ভূমিকা", "ফটোকেমিক্যাল স্মোগের জন্য দায়ী নয়"],
                [<Code>CH₄</Code>, "গ্রিনহাউস গ্যাস (১৯% ভূমিকা), CO₂-এর তুলনায় ২৫ গুণ শক্তিশালী", "প্রাকৃতিক গ্যাসের প্রধান উপাদান"],
                [<Code>N₂O</Code>, "গ্রিনহাউস গ্যাস (৫% ভূমিকা), CO₂-এর তুলনায় ২৭০ গুণ শক্তিশালী", "-"],
                [<Code>O₃</Code>, "স্ট্র্যাটোস্ফিয়ারে UV থেকে সুরক্ষা দেয়, গ্রিনহাউস প্রভাবে ৮% ভূমিকা", "ফটোকেমিক্যাল স্মোগ সৃষ্টিতে ভূমিকা রাখে"],
                [<Code>SO₂</Code>, "অ্যাসিড বৃষ্টির জন্য প্রধানত দায়ী", "শ্বাসতন্ত্রে প্রদাহ, ব্রঙ্কাইটিস ও হাঁপানি সৃষ্টি করে"]
            ]} />
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">২. স্বাস্থ্যের প্রতি ক্ষতিকারক অন্যান্য গ্যাস ও মিশ্রণ</h3>
             <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>কার্বন মনোক্সাইড (CO):</strong> নীরব ঘাতক গ্যাস, রক্তে অক্সিজেন বহন ক্ষমতা হ্রাস করে।</li>
                <li><strong>স্মোগ (Smog):</strong> ধোঁয়া ও কুয়াশার মিশ্রণ, শ্বসনতন্ত্রে অস্বাভাবিকতা সৃষ্টি করে।</li>
             </ul>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">৩. ভারী ধাতু ও বিষাক্ত রাসায়নিকের স্বাস্থ্যগত প্রভাব</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>আর্সেনিক (As):</strong> মিউটেজেনিক ও কার্সিনোজেনিক প্রভাব। নিরাপদ মাত্রা <Code>0.01 mg/L</Code>।</li>
                <li><strong>ক্যাডমিয়াম (Cd):</strong> অস্টিওপোরোসিস, কিডনি ক্ষতি। প্রাণঘাতী মাত্রা <Code>1 গ্রাম</Code>।</li>
                <li><strong>সিসা (Lead, Pb):</strong> স্নায়বিক ক্ষতি, হাড় ও দাঁতে জমা হয়।</li>
                <li><strong>ক্রোমিয়াম (Cr):</strong> ক্রোমিয়াম (VI) ক্যান্সার সৃষ্টিকারী।</li>
            </ul>
        </>
    },
    {
        id: 'greenhouse-gases',
        title: 'গ্রীনহাউজ গ্যাস',
        content: <>
            <p className="mb-4">গ্রীনহাউজ গ্যাস, তাদের প্রভাব এবং বায়ুমণ্ডলে তাদের ভূমিকা সম্পর্কে বিস্তারিত তথ্য।</p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">১. গ্রীনহাউস প্রভাবের ধারণা</h3>
            <p>গ্রীনহাউস গ্যাসগুলি সূর্য থেকে আসা দৃশ্যমান আলোকরশ্মিকে পৃথিবীতে আসতে দেয় কিন্তু পৃথিবী থেকে নির্গত দীর্ঘ তরঙ্গদৈর্ঘ্যের অবলোহিত বিকিরণ (IR) শোষণ করে পৃথিবীর তাপমাত্রা বৃদ্ধি করে।</p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">২. প্রধান গ্রীনহাউস গ্যাসসমূহ ও তাদের ভূমিকা</h3>
             <Table headers={["গ্রীনহাউস গ্যাস", "ভূমিকা (%)", "CO₂ এর তুলনায় প্রভাব (গুণ)"]} data={[
                [<Code>CO₂</Code>, "৫০%", "১ গুণ"],
                [<Code>CH₄</Code>, "১৯%", "২৫ গুণ"],
                ["CFC", "১৬%", "১৫,০০০ গুণ"],
                [<Code>O₃</Code>, "৮%", "১০ গুণ"],
                [<Code>N₂O</Code>, "৫%", "২৭০ গুণ"],
                ["জলীয় বাষ্প", "২%", "০.২ গুণ"]
             ]} />
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">৩. গ্রীনহাউস গ্যাস নয় এমন গ্যাস</h3>
             <p>নাইট্রোজেন (<Code>N₂</Code>) কোনো গ্রীনহাউস গ্যাস নয়।</p>
        </>
    },
     {
        id: 'phenol',
        title: 'ফেনল (Phenol)',
        content: <>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">ফেনলের সংজ্ঞা ও গঠন</h3>
            <p>যখন কোনো জৈব যৌগে বেনজিন চক্রের সাথে হাইড্রক্সিল মূলক (<Code>-OH</Code>) যুক্ত থাকে, তখন সেই যৌগকে ফেনল বলা হয়। সরলতম রূপ: <Code>C₆H₅OH</Code>।</p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">ফেনলের রাসায়নিক ধর্ম ও বৈশিষ্ট্য</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>অম্লত্ব:</strong> ফেনল দুর্বল অ্যাসিড।</li>
                <li><strong>নির্দেশক প্রকৃতি:</strong> <Code>-OH</Code> মূলকটি হলো অর্থো (<Code>o-</Code>) এবং প্যারা (<Code>p-</Code>) নির্দেশক এবং তীব্র সক্রিয়কারক।</li>
            </ul>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">ফেনলের গুরুত্বপূর্ণ বিক্রিয়া</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>বেঞ্জিন প্রস্তুতি:</strong> ফেনলকে উত্তপ্ত জিঙ্ক ধূলার (<Code>Zn</Code> Dust) সাথে পাতন করলে বেঞ্জিন (<Code>C₆H₆</Code>) উৎপন্ন হয়।</li>
                <li><strong>পিক্রিক অ্যাসিড:</strong> ফেনলের একটি গুরুত্বপূর্ণ জাতক হলো 2, 4, 6-ট্রাইনাইট্রোফেনল, যা পিক্রিক অ্যাসিড নামে পরিচিত।</li>
            </ul>
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">ফেনল সংশ্লিষ্ট অন্যান্য যৌগ</h3>
             <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>ক্রেসল (Cresol):</strong> মিথাইল ফেনলকে ক্রেসল বলা হয়। জীবাণুনাশক হিসেবে ব্যবহৃত হয়।</li>
                <li><strong>ডেটল উপাদান:</strong> 4-ক্লোরো-3,5-ডাইমিথাইল ফেনল (PCMC) হলো ডেটল-এর একটি গুরুত্বপূর্ণ উপাদান।</li>
             </ul>
        </>
    },
    {
        id: 'amines',
        title: 'অ্যামিন (Amine)',
        content: <>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">১. অ্যামিনের সংজ্ঞা ও কার্যকরী মূলক</h3>
            <p>অ্যামিন হলো সেই যৌগ যেখানে অ্যামোনিয়া (<Code>NH₃</Code>) অণুর এক বা একাধিক হাইড্রোজেন পরমাণু অ্যালকাইল (<Code>R</Code>) বা অ্যারাইল (<Code>Ar</Code>) মূলক দ্বারা প্রতিস্থাপিত হয়। কার্যকরী মূলক হলো অ্যামিনো মূলক (<Code>-NH₂</Code>)।</p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">২. অ্যামিনের প্রকারভেদ</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>প্রাইমারি অ্যামিন (1° Amine):</strong> <Code>R-NH₂</Code> (যেমন: মিথাইল অ্যামিন)</li>
                <li><strong>সেকেন্ডারি অ্যামিন (2° Amine):</strong> <Code>R₂NH</Code> (যেমন: ডাই মিথাইল অ্যামিন)</li>
                <li><strong>টারশিয়ারি অ্যামিন (3° Amine):</strong> <Code>R₃N</Code></li>
            </ul>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">৩. অ্যামিনের গুরুত্বপূর্ণ বিক্রিয়া</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>কার্বিল অ্যামিন বিক্রিয়া:</strong> প্রাইমারি অ্যামিন শনাক্ত করতে ব্যবহৃত হয়। দুর্গন্ধযুক্ত ফিনাইল আইসো সায়ানাইড (<Code>R-N≡C</Code>) উৎপন্ন হয়।</li>
                <li><strong>হফম্যান ডিগ্রেডেশন বিক্রিয়া:</strong> অ্যামাইড থেকে প্রাইমারি অ্যামিন প্রস্তুত করা হয় এবং কার্বন শৃঙ্খল থেকে একটি কার্বন পরমাণু কমানো যায়।</li>
            </ul>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">৪. অ্যামাইনো অ্যাসিড</h3>
            <p>অ্যামাইনো অ্যাসিড হলো প্রোটিনের গাঠনিক একক। এতে অ্যামিনো (<Code>-NH₂</Code>) এবং কার্বক্সিলিক (<Code>-COOH</Code>) মূলক উভয়ই থাকে। অ্যামাইনো অ্যাসিডসমূহ পেপটাইড বন্ধনী (<Code>-CONH-</Code>) দ্বারা যুক্ত হয়ে প্রোটিন গঠন করে।</p>
        </>
    },
    {
        id: 'colloids',
        title: 'কলয়েড, সাসপেনশন, ইমালশন',
        content: <>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">১. কলয়েড (Colloid)</h3>
            <p>কলয়েড হলো এমন একটি সিস্টেম যেখানে কণাগুলির ব্যাস <Code>1 nm</Code> থেকে <Code>100 nm</Code> এর মধ্যে হয়। এটি টিণ্ডাল প্রভাব ও ব্রাউনীয় গতি প্রদর্শন করে।</p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">২. কোলয়েড সিস্টেমের প্রকারভেদ</h3>
            <Table headers={["নাম", "বিচ্ছুরিত দশা", "বিচ্ছুরণ মাধ্যম", "উদাহরণ"]} data={[
                ["ইমালশন (Emulsion)", "তরল", "তরল", "দুধ, মাখন"],
                ["জেল (Gel)", "তরল", "কঠিন", "জেলি"],
                ["এরোসল (Aerosol)", "তরল/কঠিন", "গ্যাস", "ধোঁয়া, কুয়াশা"],
                ["ফোম (Foam)", "গ্যাস", "তরল", "সাবানের ফেনা"],
                ["সল (Sol)", "কঠিন", "তরল", "রঞ্জক"]
            ]} />
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">৩. ইমালশন (Emulsion)</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>জল-এ-তেল (O/W):</strong> তেল হলো বিচ্ছুরিত দশা, জল হলো মাধ্যম। উদাহরণ: দুধ।</li>
                <li><strong>তেল-এ-জল (W/O):</strong> জল হলো বিচ্ছুরিত দশা, তেল হলো মাধ্যম। উদাহরণ: মাখন।</li>
            </ul>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">৪. কোয়াগুলেশন (Coagulation)</h3>
            <p>এটি এমন একটি প্রক্রিয়া যার মাধ্যমে কোলয়েড কণাগুলিকে তড়িৎ বিশ্লেষ্য যোগ করে জমাট বাঁধিয়ে অধঃক্ষিপ্ত করা হয়।</p>
        </>
    },
    {
        id: 'etp',
        title: 'ETP (Effluent Treatment Plant)',
        content: <>
            <p>ETP বা Effluent Treatment Plant হলো শিল্প কারখানার বর্জ্য জল দূষণমুক্ত করার একটি প্রক্রিয়া।</p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">গুরুত্ব</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li>জল দূষণ এবং দুর্গন্ধ দূষণ কমানোর জন্য ETP স্থাপন করা প্রয়োজন।</li>
                <li>বাংলাদেশে ট্যানারি, ডাইং এবং কাগজ শিল্পে ETP বসানো বাধ্যতামূলক।</li>
            </ul>
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">কার্যপ্রণালী</h3>
             <p>এর কার্য প্রক্রিয়ার মধ্যে রয়েছে তড়িৎ বিশ্লেষণ, অধঃক্ষেপণ এবং জৈব ক্রিয়া।</p>
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">রিসাইকেল ও পুনর্ব্যবহার</h3>
             <ul className="list-disc list-inside space-y-2 mb-4">
                <li>তামা এবং কাঁচ হলো ১০০% রিসাইকেলযোগ্য পদার্থ।</li>
                <li>বর্জ্য জলের পুনর্ব্যবহারের মাধ্যমে শিল্প কারখানায় জলের প্রয়োজনীয়তার ৪০% সাশ্রয় করা যায়।</li>
             </ul>
        </>
    },
    {
        id: 'directing-groups',
        title: 'অর্থো-প্যারা-মেটা নির্দেশক মূলক',
        content: <>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">১. অর্থো-প্যারা নির্দেশক মূলক (Ortho-Para Directing)</h3>
            <p>এগুলি সাধারণত বলয় সক্রিয়কারক (Ring Activating) গ্রুপ এবং বেনজিন বলয়ে ইলেকট্রন ঘনত্ব বাড়িয়ে দেয়।</p>
            <Table headers={["মূলকের নাম", "সংকেত", "প্রভাব"]} data={[
                ["হাইড্রক্সিল মূলক", <Code>-OH</Code>, "সক্রিয়কারক"],
                ["অ্যামিনো মূলক", <Code>-NH₂</Code>, "তীব্র সক্রিয়কারক"],
                ["অ্যালকাইল মূলক", <Code>-R</Code>, "সক্রিয়কারক"],
                ["হ্যালোজেন মূলক", <Code>-X</Code>, "অর্থো-প্যারা, কিন্তু নিষ্ক্রিয়কারক (ব্যতিক্রম)"]
            ]} />
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">২. মেটা নির্দেশক মূলক (Meta Directing)</h3>
             <p>এগুলি বলয় নিষ্ক্রিয়কারক (Ring Deactivating) গ্রুপ এবং বেনজিন বলয় থেকে ইলেকট্রন ঘনত্ব টেনে নেয়।</p>
             <Table headers={["মূলকের নাম", "সংকেত", "প্রভাব"]} data={[
                ["নাইট্রো মূলক", <Code>-NO₂</Code>, "নিষ্ক্রিয়কারক"],
                ["কার্বক্সিলিক মূলক", <Code>-COOH</Code>, "নিষ্ক্রিয়কারক"],
                ["অ্যালডিহাইড মূলক", <Code>-CHO</Code>, "নিষ্ক্রিয়কারক"],
                ["সায়ানো মূলক", <Code>-CN</Code>, "নিষ্ক্রিয়কারক"],
             ]} />
        </>
    },
    {
        id: 'n-fixation',
        title: 'নাইট্রোজেন ফিক্সেশন (N-Fixation)',
        content: <>
            <p>নাইট্রোজেন সংবন্ধন হলো সেই প্রক্রিয়া, যার মাধ্যমে বায়ুমণ্ডলের নাইট্রোজেন গ্যাস (<Code>N₂</Code>) জীবের ব্যবহার উপযোগী যৌগে রূপান্তরিত হয়।</p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">১. নাইট্রোজেন সংবন্ধনে অংশগ্রহণকারী অনুজীব</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>সহজীবী:</strong> রাইজোবিয়াম (Rhizobium) শিম জাতীয় উদ্ভিদের মূলে নাইট্রোজেন সংবন্ধন করে।</li>
                <li><strong>মুক্তজীবী:</strong> অ্যাজোটোব্যাক্টার (Azotobacter) এবং ক্লস্ট্রিডিয়াম (Clostridium) মাটিতে মুক্তভাবে নাইট্রোজেন সংবন্ধন করে।</li>
            </ul>
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">২. নাইট্রিফিকেশন (Nitrification) প্রক্রিয়া</h3>
             <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>নাইট্রোসমোনাস (Nitrosomonas):</strong> অ্যামোনিয়া (<Code>NH₃</Code>) কে নাইট্রাইট (<Code>NO₂⁻</Code>) এ রূপান্তরিত করে।</li>
                <li><strong>নাইট্রোব্যাক্টার (Nitrobacter):</strong> নাইট্রাইট (<Code>NO₂⁻</Code>) কে নাইট্রেট (<Code>NO₃⁻</Code>) এ রূপান্তরিত করে।</li>
            </ul>
            <p>উদ্ভিদ সাধারণত নাইট্রোজেনকে নাইট্রেট আয়ন (<Code>NO₃⁻</Code>) রূপে শোষণ করে।</p>
        </>
    },
    {
        id: 'redox-reactions',
        title: 'জারণ-বিজারণ (Redox Reaction)',
        content: <>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">১. জারণ ও বিজারণের সংজ্ঞা</h3>
            <Table headers={["প্রক্রিয়া", "জারণ (Oxidation)", "বিজারণ (Reduction)"]} data={[
                ["ইলেকট্রন", "ইলেকট্রন বর্জন", "ইলেকট্রন গ্রহণ"],
                ["জারণ মান", "বৃদ্ধি পায়", "হ্রাস পায়"],
            ]} />
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">২. জারক ও বিজারক পদার্থ</h3>
              <Table headers={["বৈশিষ্ট্য", "জারক (Oxidizing Agent)", "বিজারক (Reducing Agent)"]} data={[
                ["ইলেকট্রন", "গ্রহণ করে", "বর্জন করে"],
                ["পরিণতি", "নিজে বিজারিত হয়", "নিজে জারিত হয়"],
            ]} />
             <p className="mt-4"><strong>জারকের উদাহরণ:</strong> <Code>O₂</Code>, <Code>Cl₂</Code>, <Code>KMnO₄</Code>, <Code>K₂Cr₂O₇</Code>, <Code>HNO₃</Code></p>
             <p><strong>বিজারকের উদাহরণ:</strong> <Code>H₂</Code>, <Code>Zn</Code>, <Code>Fe</Code>, <Code>H₂S</Code>, <Code>FeCl₂</Code></p>
             <p><strong>জারক ও বিজারক উভয়:</strong> <Code>SO₂</Code>, <Code>H₂O₂</Code>, <Code>O₃</Code></p>
        </>
    },
    {
        id: 'acid-rain',
        title: 'অ্যাসিড বৃষ্টি (Acid Rain)',
        content: <>
            <p>বৃষ্টির জলের <Code>pH</Code> মান <Code>5.6</Code> এর কম হলে, তাকে অ্যাসিড বৃষ্টি বলা হয়।</p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">কারণ</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li>প্রধানত দায়ী গ্যাসগুলি হলো সালফার ডাই-অক্সাইড (<Code>SO₂</Code>) এবং নাইট্রোজেন ডাই-অক্সাইড (<Code>NO₂</Code>)।</li>
                <li>এরা জলের সাথে বিক্রিয়া করে সালফিউরিক অ্যাসিড (<Code>H₂SO₄</Code>) এবং নাইট্রিক অ্যাসিড (<Code>HNO₃</Code>) তৈরি করে।</li>
            </ul>
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">ক্ষতিকারক প্রভাব</h3>
             <ul className="list-disc list-inside space-y-2 mb-4">
                <li>জলজ জীবন ধ্বংস করে।</li>
                <li>গাছপালা ও বনভূমির ক্ষতি করে।</li>
                <li>দালান ও ইমারতগুলির ক্ষতি করে।</li>
             </ul>
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">প্রতিকার</h3>
             <ul className="list-disc list-inside space-y-2 mb-4">
                <li>শিল্পে ফ্লু গ্যাস ডিসালফারাইজেশন (FGD) প্রযুক্তি ব্যবহার করা।</li>
                <li>যানবাহনে অনুঘটক রূপান্তরক (Catalytic Converters) ব্যবহার করা।</li>
                <li>মাটির অম্লত্ব দূর করতে চুন (<Code>CaO</Code>) প্রয়োগ করা।</li>
             </ul>
        </>
    },
    {
        id: 'milk-chemistry',
        title: 'দুধ এবং দুগ্ধজাত খাদ্য',
        content: <>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">১. দুধের প্রকৃতি ও উপাদান</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li>দুধ হলো জল-এ-তেল (<Code>O/W</Code>) ইমালশন।</li>
                <li>প্রধান প্রোটিন: কেসিন (Casein)।</li>
                <li>শর্করা: ল্যাকটোজ (গ্লুকোজ + গ্যালাকটোজ)। মানুষের দুধে ল্যাকটোজের পরিমাণ সর্বাধিক (<Code>7.1%</Code>)।</li>
                <li>ভিটামিন-এ বেশি থাকে, কিন্তু ভিটামিন-ডি ও সি কম থাকে।</li>
            </ul>
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">২. দুগ্ধজাত খাদ্য ও প্রক্রিয়াকরণ</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>ছানা তৈরি:</strong> দুধ থেকে ছানা তৈরির পদ্ধতিকে কোয়াগুলেশন বলে।</li>
                <li><strong>মাখন:</strong> এটি তেল-এ-জল (<Code>W/O</Code>) ইমালশন।</li>
                <li><strong>পাস্তুরায়ন:</strong> <Code>70°C</Code> থেকে <Code>80°C</Code> তাপমাত্রায় উত্তপ্ত করে জীবাণু ধ্বংস করা।</li>
            </ul>
             <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">৩. পরিপাক ও পরিমাপ</h3>
             <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>ল্যাকটেজ (Lactase):</strong> ল্যাকটোজ পরিপাককারী এনজাইম।</li>
                <li><strong>ল্যাকটোমিটার (Lactometer):</strong> দুধের আপেক্ষিক গুরুত্ব পরিমাপক যন্ত্র।</li>
             </ul>
        </>
    },
    {
        id: 'primary-secondary-substances',
        title: 'প্রাথমিক ও সেকেন্ডারি পদার্থ',
        content: <>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">১. রসায়নে প্রমাণ পদার্থ</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>প্রাথমিক প্রমাণ পদার্থ:</strong> বিশুদ্ধ, স্থায়ী এবং সরাসরি মেপে দ্রবণ প্রস্তুত করা যায়। <br/>উদাহরণ: <Code>Na₂CO₃</Code>, <Code>K₂Cr₂O₇</Code>, <Code>H₂C₂O₄·2H₂O</Code>।</li>
                <li><strong>সেকেন্ডারি প্রমাণ পদার্থ:</strong> বিশুদ্ধ বা স্থায়ী নয়, এদের ঘনমাত্রা টাইট্রেশনের মাধ্যমে নির্ণয় করতে হয়। <br/>উদাহরণ: <Code>KMnO₄</Code>, <Code>H₂SO₄</Code>, <Code>NaOH</Code>।</li>
            </ul>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">২. জীববিজ্ঞানে ভাজক টিস্যু</h3>
            <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>প্রাইমারি ভাজক টিস্যু:</strong> উদ্ভিদের শীর্ষস্থানিক ও নিবেশিত ভাজক টিস্যু থেকে উৎপন্ন হয়।</li>
                <li><strong>সেকেন্ডারি ভাজক টিস্যু:</strong> উদ্ভিদের পার্শ্বীয় ভাজক টিস্যু গঠন করে।</li>
            </ul>
        </>
    },
    {
        id: 'flame-test',
        title: 'শিখা পরীক্ষা (Flame Test)',
        content: <>
            <p>শিখা পরীক্ষা হলো কিছু ধাতব ক্যাটায়ন শনাক্ত করার পদ্ধতি, যেখানে ধাতব লবণকে শিখায় ধরলে নির্দিষ্ট বর্ণ বিকিরণ করে।</p>
            <Table headers={["ধাতব মৌল", "প্রতীক", "খালি চোখে শিখার বর্ণ"]} data={[
                ["সোডিয়াম", <Code>Na</Code>, "সোনালি হলুদ (Golden Yellow)"],
                ["পটাসিয়াম", <Code>K</Code>, "হালকা বেগুনি (Light Violet)"],
                ["ক্যালসিয়াম", <Code>Ca</Code>, "ইটের মতো লাল (Brick Red)"],
                ["স্ট্রনসিয়াম", <Code>Sr</Code>, "গাড়ো লাল (Deep Red)"],
                ["বেরিয়াম", <Code>Ba</Code>, "হলুদ সবুজ (Yellowish Green)"],
                ["কপার", <Code>Cu</Code>, "নীলাভ সবুজ (Bluish Green)"],
                ["সীসা", <Code>Pb</Code>, "নীলাভ সাদা (Bluish White)"]
            ]} />
        </>
    },
    {
        id: 'heavy-metals-food-chain',
        title: 'খাদ্যশৃঙ্খলে ভারী ধাতু',
        content: <>
            <p>ভারী ধাতু হলো সেসব ধাতব মৌল যাদের ঘনত্ব বেশি এবং সামান্য উপস্থিতিই বিষক্রিয়া সৃষ্টি করে।</p>
            <Table headers={["ভারী ধাতু", "প্রবেশের প্রধান পথ", "স্বাস্থ্যের উপর প্রধান প্রভাব"]} data={[
                ["সিসা (Pb)", "লেড স্টোরেজ ব্যাটারি, PVC", "মৃত সন্তান প্রসব, শিশুর বুদ্ধি হ্রাস"],
                ["ক্রোমিয়াম (Cr)", "ট্যানারি শিল্পের বর্জ্য", "রক্তশূন্যতা, Cr(VI) কার্সিনোজেন"],
                ["ক্যাডমিয়াম (Cd)", "আকরিক নিষ্কাশন, ঝালাই", "অস্টিওপোরোসিস (হাড় ভঙ্গুরতা)"],
                ["আর্সেনিক (As)", "মাটিতে বিদ্যমান লবণ", "ব্ল্যাক ফুট ডিজিজ, ক্যান্সার"]
            ]} />
        </>
    },
    {
        id: 'hazard-symbols',
        title: 'হ্যাজার্ড সিম্বল (Hazard Symbol)',
        content: <>
            <p>হ্যাজার্ড সিম্বল কোনো রাসায়নিকের বিপজ্জনক প্রকৃতি নির্দেশ করে। আন্তর্জাতিকভাবে স্বীকৃত হ্যাজার্ড সিম্বলের সংখ্যা ১০টি।</p>
            <Table headers={["প্রকৃতি", "প্রতীক/চিত্র", "ক্ষতিকারক প্রভাব"]} data={[
                ["ক্ষতিকারক (Harmful)", <div className="text-3xl font-bold text-center text-orange-500">Xn</div>, "শ্বাসনালী ও ত্বকে প্রদাহ সৃষ্টি করে"],
                ["উত্তেজক (Irritant)", <div className="text-3xl font-bold text-center text-orange-500">Xi</div>, "ত্বক, চোখ বা শ্বাসনালীতে প্রদাহ"],
                ["বিষাক্ত (Toxic)", "মাথার খুলি ও দুটি হাড়", "গুরুতর ক্ষতি বা মৃত্যু ঘটাতে পারে"],
                ["দাহ্য (Flammable)", "শিখার ছবি", "সহজে জ্বলে উঠতে পারে"],
                ["বিস্ফোরক (Explosive)", "বিস্ফোরণের ছবি", "তাপ বা ঘর্ষণে বিস্ফোরণ ঘটাতে পারে"],
                ["পরিবেশ দূষক", "মৃত মাছ ও গাছের ছবি", "পরিবেশের ক্ষতি করে"],
                ["তেজস্ক্রিয় (Radioactive)", "তেজস্ক্রিয়তার প্রতীক (Trefoil)", "তেজস্ক্রিয় বিকিরণের বিপদ"]
            ]} />
        </>
    },
    {
        id: 'dettol',
        title: 'ডেটল (Dettol)',
        content: <>
            <p>ডেটল হলো একটি জীবাণুনাশক এবং পূতিবারক (অ্যান্টিসেপটিক) যা র‍্যাকিট বেঙ্কিসার কোম্পানির একটি পণ্য।</p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-700">মূল উপাদান</h3>
            <p>ডেটলের প্রধান উপাদান হলো <Code>4-ক্লোরো-3,5-ডাইমিথাইল ফেনল</Code> (PCMC - Para Chloro Meta Cresol)।</p>
        </>
    }
];